import React from 'react';
import PropTypes from 'prop-types';
import {propOr, mergeRight} from 'ramda';
import Markdown from 'react-markdown';

import {getIconCategory, getFontSize, getBorderRadius} from '../utils';

/**
 * A layout component for displaying a tag with an optional icon.
 *
 * **Example Usage**
 * ```
 * ddk.Tag(text="**Data Updated:** Oct. 8, 2023", icon="circle-check", radius="lg"),
 * ```
 */
function Tag(props) {
    const {
        id,
        text,
        icon,
        radius,
        children,
        margin,
        padding,
        background_color,
        border_width,
        border_style,
        border_color,
        box_shadow,
        font_color,
        font_family,
        font_size,
    } = props;

    /* merge conditional styles from props */
    const style = mergeRight(
        {
            padding: padding,
            margin: margin,
            backgroundColor: background_color,
            borderWidth: border_width,
            borderStyle: border_style,
            borderColor: border_color,
            borderRadius: getBorderRadius(radius),
            boxShadow: box_shadow,
            color: font_color,
            fontFamily: font_family,
            fontSize: getFontSize(font_size),
        },
        propOr({}, 'style', props)
    );

    const category = getIconCategory(icon?.category);

    return (
        <div
            className={`layout-tag-container ${propOr('', 'className', props)}`}
            id={id}
            style={style}
        >
            {icon &&
                (typeof icon === 'string' ? (
                    <i className={`fa${category} fa-${icon}`}></i>
                ) : (
                    <i className={`fa${category} fa-${icon?.name}`}></i>
                ))}
            {text && (
                <span>
                    <Markdown>{text}</Markdown>
                </span>
            )}
            {children}
        </div>
    );
}

Tag.propTypes = {
    /**
     * The ID of this component, used to identify Dash components
     * in callbacks. The ID needs to be unique across all of the
     * components in an app.
     */
    id: PropTypes.string,

    /**
     * Optional user-defined CSS class for the Tag
     */
    className: PropTypes.string,

    /**
     * An optional prop to pass any additional children to the Tag.
     */
    children: PropTypes.node,

    /**
     * The content, which may include Markdown formatting, to display in the Tag.
     */
    text: PropTypes.string,

    /**
     * An optional icon to display on the tag, which can be either a string
     * representing a Font Awesome icon name (without the "fa-"
     * prefix) or an object specifying the icon name and category.
     */
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            /**
             * The name of the Font Awesome icon without the "fa-" prefix.
             */
            icon_name: PropTypes.string,

            /**
             * The Font Awesome category for the icon, which must be one of the following:
             * - "solid"
             * - "regular"
             * - "brands"
             * - "light"
             */
            icon_category: PropTypes.oneOf([
                // PropTypes have to be inline in js, unhandled imports by react-docgen.
                'solid',
                'regular',
                'brands',
                'light',
            ]),
        }),
    ]),

    /**
     * The border radius for the Tag: "sm" (0.125rem), "md" (0.25rem), "lg" (0.75rem), or "none".
     */
    radius: PropTypes.oneOf(['sm', 'md', 'lg', 'none']),

    /**
     * A special property used for identifying and distinguishing elements in a list or a set of rendered components.
     * This should be passed when rendering a list of tags within another component.
     */
    key: PropTypes.string,

    /**
     * Space (in pixels) surrounding the Tag.
     * Overrides theme.tag_margin.
     */
    margin: PropTypes.number,

    /**
     * Space (in pixels) on the inside of the Tag, between the border
     * and the edge of the content.
     * Overrides theme.tag_padding.
     */
    padding: PropTypes.number,

    /**
     * The background color applied to the Tag. Overrides theme.Tag_background_color.
     */
    background_color: PropTypes.string,

    /**
     * The box shadow(s) applied to the Tag. Overrides theme.tag_box_shadow.
     */
    box_shadow: PropTypes.string,

    /**
     * The border width applied to the Tag. Overrides theme.tag_border.width.
     */
    border_width: PropTypes.string,

    /**
     * The border style applied to the Tag. Overrides theme.tag_border.style.
     */
    border_style: PropTypes.string,

    /**
     * The border color applied to the Tag. Overrides theme.tag_border.color.
     */
    border_color: PropTypes.string,

    /**
     * The font color of Tag children. Overrides theme.tag_text
     */
    font_color: PropTypes.string,

    /**
     * The font family of Tag children. Overrides theme.font_family_tag
     */
    font_family: PropTypes.string,

    /**
     * The font size of Tag children.
     * Can be a relative size, "smaller", "normal", "larger",
     * or a CSS attribute size (e.g. `px`, 'em`)
     */
    font_size: PropTypes.string,

    /**
     * The style object of the outermost div of the Tag.
     * Use this to override e.g. the Tag's height
     * (`{'height': '100px'}`)
     */
    style: PropTypes.object,
};

export default Tag;
